export interface TradeRatioStats {
  group: string
  netProfit: number
  winRate: number | null
  profitFactor: number | null
  maxDrawdownPercentage: number | null
  closedTradeCount: number
}

export interface TradeRatiosOverview {
  total: number
  profitableCount: number
  minProfit: number
  maxProfit: number
}

export function sortSummariesByProfit<T extends { currentProfit?: number }>(summaries: T[]): T[] {
  return [...summaries].sort((a, b) => (b.currentProfit || 0) - (a.currentProfit || 0))
}

export function calculateProfitFactor(grossProfit?: number, grossLoss?: number): number | null {
  if (grossLoss) return (grossProfit || 0) / Math.abs(grossLoss)
  return grossProfit ? Infinity : null
}

export function getTradeRatioStats(summary: any): TradeRatioStats {
  const closedTradeCount = summary.closedTradeCount || 0
  return {
    group: summary.group,
    netProfit: summary.currentProfit || 0,
    winRate: closedTradeCount ? ((summary.profitTradeCount || 0) / closedTradeCount) * 100 : null,
    profitFactor: calculateProfitFactor(summary.grossProfit, summary.grossLoss),
    maxDrawdownPercentage:
      summary.maxRelativeDrawdownPercentage ?? summary.relativeDrawdownPercentage ?? null,
    closedTradeCount,
  }
}

export function getTradeRatiosOverview(summaries: any[]): TradeRatiosOverview {
  const profits = summaries.map((s) => s.currentProfit || 0)
  return {
    total: summaries.length,
    profitableCount: profits.filter((p) => p > 0).length,
    minProfit: Math.min(...profits),
    maxProfit: Math.max(...profits),
  }
}

// Drawdown from the running peak, as a percentage of account equity at that peak.
export function toDrawdownPercentages(cumulativeProfit: number[], accountEquity: number): number[] {
  let peak = -Infinity
  return cumulativeProfit.map((profit) => {
    peak = Math.max(peak, profit)
    const peakEquity = accountEquity + peak
    return peakEquity > 0 ? ((profit - peak) / peakEquity) * 100 : 0
  })
}

export function formatCompactCurrency(value: number, signed = false): string {
  const sign = value < 0 ? '−' : signed && value > 0 ? '+' : ''
  const formatted = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: Math.abs(value) >= 1000 ? 1 : 0,
  }).format(Math.abs(value))
  return `${sign}$${formatted}`
}

// "Jan '24" — the apostrophe marks the year so it isn't read as a day of the month.
export function formatMonthYear(value: Date | number): string {
  const date = new Date(value)
  const month = date.toLocaleDateString(undefined, { month: 'short' })
  return `${month} '${String(date.getFullYear()).slice(-2)}`
}

export function formatPercentage(value: number | null, fractionDigits = 1): string {
  if (value == null) return '—'
  const sign = value < 0 ? '−' : ''
  return `${sign}${Math.abs(value).toFixed(fractionDigits)}%`
}

export function formatProfitFactor(value: number | null): string {
  if (value == null) return '—'
  if (!Number.isFinite(value)) return '∞'
  return value.toFixed(2)
}
